import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Add, Edit, Visibility, Delete, Person } from "@mui/icons-material";
import { GoalsListSkeleton } from "../components/ui/PageContentSkeleton";
import { goalStatusColor, goalStatusLabel, stripHtml } from "../utils/goals";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { hasNavWrite, isPrivilegedNavRole } from "../utils/navPermissions";

const msPerDay = 86400000;

function dueDateToTimeMs(dueDate) {
  if (!dueDate) return null;
  const s = String(dueDate).slice(0, 10); // YYYY-MM-DD
  const t = Date.parse(`${s}T00:00:00.000Z`);
  return Number.isFinite(t) ? t : null;
}

function getDueTimelineMeta(dueDate) {
  const dueMs = dueDateToTimeMs(dueDate);
  if (dueMs === null) return null;

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const todayMs = today.getTime();

  const diffDays = Math.round((dueMs - todayMs) / msPerDay);
  if (diffDays === 0) return { kind: "today", text: "Due today" };
  if (diffDays < 0) return { kind: "overdue", text: "Overdue" };
  return { kind: "future", text: `Due in ${diffDays} days` };
}

/**
 * Goals list (outer view): show only the first block of the rich description — same as the
 * first line/paragraph on the detail page. Later paragraphs or list rows are omitted (not merged).
 */
function goalDescriptionListPreviewHtml(raw) {
  const cleaned = DOMPurify.sanitize(raw || "");
  if (!cleaned.trim()) return "";

  // Quill: one or more <p>...</p>. Use the first paragraph that has visible text.
  const pTag = /<p\b[^>]*>[\s\S]*?<\/p>/gi;
  let m;
  while ((m = pTag.exec(cleaned)) !== null) {
    const frag = m[0];
    if (stripHtml(frag).replace(/\s+/g, "").length) {
      return DOMPurify.sanitize(frag);
    }
  }

  // If the description starts with a list, show only the first item (first "line" of that block).
  const liMatch = cleaned.match(/<li\b[^>]*>[\s\S]*?<\/li>/i);
  if (liMatch && stripHtml(liMatch[0]).replace(/\s+/g, "").length) {
    return DOMPurify.sanitize(liMatch[0]);
  }

  // No block tags: treat whole snippet as one line of preview
  return cleaned;
}

/** Manager/admin: "mine" = own goals only; "team" = pick an employee, goals load per employee. */
export default function Goals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managedEmployees, setManagedEmployees] = useState([]);
  const [section, setSection] = useState("mine");
  /** Explicit pick in Employee goals; falls back to first employee via `resolvedTeamId`. */
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const myId = user?.id || user?._id;
  const role = (user?.role || "EMPLOYEE").toUpperCase();
  const isAdmin = role === "ADMIN";
  const isManager = role === "MANAGER";
  const canViewOthersGoals = isAdmin || isManager;
  const showTeamUi = canViewOthersGoals && managedEmployees.length > 0;
  const canMutateGoals =
    isPrivilegedNavRole(user?.role) || hasNavWrite(user, "/goals");

  useEffect(() => {
    if (!canViewOthersGoals || !myId) {
      setManagedEmployees([]);
      return;
    }

    let cancelled = false;

    const mapUserRow = (u) => ({
      _id: u.id || u._id,
      id: u.id || u._id,
      name: u.name,
      email: u.email,
      role: u.role,
    });

    const loadFromUsers = async () => {
      const usersRes = await api.get("/users");
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      return users
        .filter(
          (u) =>
            (u.role || "").toUpperCase() !== "ADMIN" &&
            String(u.id || u._id) !== String(myId)
        )
        .map(mapUserRow)
        .sort((a, b) =>
          String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""))
        );
    };

    (async () => {
      try {
        const res = await api.get("/manager/dashboard");
        const data = res.data?.data || res.data;
        let list = Array.isArray(data?.managedEmployees) ? data.managedEmployees : [];

        if (isAdmin && list.length === 0) {
          list = await loadFromUsers();
        } else {
          list = [...list].sort((a, b) =>
            String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""))
          );
        }

        if (!cancelled) setManagedEmployees(list);
      } catch {
        if (!cancelled) {
          if (isAdmin) {
            try {
              const list = await loadFromUsers();
              if (!cancelled) setManagedEmployees(list);
            } catch {
              if (!cancelled) setManagedEmployees([]);
            }
          } else {
            setManagedEmployees([]);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewOthersGoals, isAdmin, myId]);

  const resolvedTeamId = useMemo(() => {
    if (!showTeamUi || section !== "team") return null;
    if (
      selectedTeamMemberId &&
      managedEmployees.some((e) => String(e.id || e._id) === String(selectedTeamMemberId))
    ) {
      return String(selectedTeamMemberId);
    }
    const first = managedEmployees[0];
    return first ? String(first.id || first._id) : null;
  }, [showTeamUi, section, selectedTeamMemberId, managedEmployees]);

  const fetchGoals = useCallback(async () => {
    if (showTeamUi && section === "team" && !resolvedTeamId) {
      setGoals([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let params = {};
      if (showTeamUi && section === "team" && resolvedTeamId) {
        params = { employee_id: resolvedTeamId };
      }
      const res = await api.get("/goals", { params });
      const raw = res.data?.data !== undefined ? res.data.data : res.data;
      setGoals(Array.isArray(raw) ? raw : []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load goals");
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, [showTeamUi, section, resolvedTeamId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const sortedGoals = useMemo(() => {
    return [...goals].sort((a, b) => {
      const aT = dueDateToTimeMs(a?.due_date);
      const bT = dueDateToTimeMs(b?.due_date);

      // Null/invalid due dates at the end.
      if (aT === null && bT === null) return 0;
      if (aT === null) return 1;
      if (bT === null) return -1;

      // Descending due date (newest/latest first).
      return bT - aT;
    });
  }, [goals]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/goals/${deleteTarget.id}`);
      toast.success("Goal deleted");
      setDeleteTarget(null);
      fetchGoals();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete goal");
    } finally {
      setDeleting(false);
    }
  };

  const selectedEmployee = managedEmployees.find(
    (e) => String(e.id || e._id) === String(resolvedTeamId)
  );

  const newGoalHref = () => {
    if (showTeamUi && section === "team") {
      if (!resolvedTeamId) {
        toast.info("No employees available.");
        return;
      }
      navigate(`/goals/create?employee_id=${resolvedTeamId}`);
      return;
    }
    navigate("/goals/create");
  };

  const goalsList = (
    <div className="grid gap-4">
      {sortedGoals.map((g) => {
        const dueMeta = g.due_date ? getDueTimelineMeta(g.due_date) : null;
        const duePillClass =
          dueMeta?.kind === "overdue"
            ? "inline-block ml-2 px-2 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50"
            : dueMeta?.kind === "today"
              ? "inline-block ml-2 px-2 py-0.5 rounded text-xs font-medium text-primary bg-primary/10"
              : dueMeta?.kind === "future"
                ? "inline-block ml-2 px-2 py-0.5 rounded text-xs font-medium text-green-700 bg-green-50"
                : "inline-block ml-2 px-2 py-0.5 rounded text-xs font-medium text-gray-600 bg-gray-50";

        return (
        <div
          key={g.id}
          className="bg-white rounded-xl shadow p-4 flex items-center justify-between flex-wrap gap-2"
        >
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg break-words line-clamp-1">{g.title}</h3>
            {stripHtml(g.description || "").trim() ? (
              <div
                className="text-sm text-gray-500 mt-0.5 min-w-0 overflow-hidden break-words leading-relaxed
                  [&_p]:m-0 [&_li]:m-0
                  [&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic [&_a]:text-primary [&_a]:underline"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  overflow: "hidden",
                }}
                title={stripHtml(g.description || "") || undefined}
                dangerouslySetInnerHTML={{
                  __html: goalDescriptionListPreviewHtml(g.description),
                }}
              />
            ) : (
              <p className="text-sm text-gray-400 mt-0.5">—</p>
            )}
            <span
              className={
                "inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium " + goalStatusColor(g.status)
              }
            >
              {goalStatusLabel(g.status)}
            </span>
            {g.due_date && (
              <span className="ml-2 text-xs text-gray-400">Due: {g.due_date}</span>
            )}
            {dueMeta && <span className={duePillClass}>{dueMeta.text}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              onClick={() => navigate(`/goals/${g.id}`)}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="View"
            >
              <Visibility className="w-4 h-4" />
            </button>
            {canMutateGoals && (
              <>
                <button
                  onClick={() => navigate(`/goals/${g.id}?edit=1`)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget({ id: g.id, title: g.title })}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                  title="Delete"
                >
                  <Delete className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );

  const emptyGoals = (
    <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
      {showTeamUi && section === "team"
        ? "No goals for this employee yet."
        : canMutateGoals
        ? "No goals yet. Create one to get started."
        : "No goals yet."}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-1">Goals</h1>
          <p className="text-gray-600">
            {showTeamUi
              ? "Use My goals for your own targets. Use Employee goals to review each team member’s goals separately."
              : "Set and track your goals."}
          </p>
        </div>
        {canMutateGoals && (
          <button
            type="button"
            onClick={newGoalHref}
            className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
          >
            <Add className="w-4 h-4" /> New Goal
          </button>
        )}
      </div>

      {showTeamUi && (
        <div className="mb-6 flex flex-wrap gap-2 p-1 bg-gray-100/80 rounded-2xl border border-gray-200 w-fit max-w-full">
          <button
            type="button"
            onClick={() => setSection("mine")}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              section === "mine"
                ? "bg-white text-primary shadow-soft border border-gray-200"
                : "text-gray-600 hover:text-primary"
            }`}
          >
            My goals
          </button>
          <button
            type="button"
            onClick={() => setSection("team")}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              section === "team"
                ? "bg-white text-primary shadow-soft border border-gray-200"
                : "text-gray-600 hover:text-primary"
            }`}
          >
            Employee goals
          </button>
        </div>
      )}

      {showTeamUi && section === "team" ? (
        <div className="flex flex-col lg:flex-row gap-6 items-stretch">
          <aside className="w-full lg:w-72 shrink-0">
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Employees
                </p>
              </div>
              <ul className="max-h-[min(60vh,28rem)] overflow-y-auto p-2 space-y-1">
                {managedEmployees.map((emp) => {
                  const id = String(emp.id || emp._id);
                  const active = String(resolvedTeamId) === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedTeamMemberId(id)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                          active
                            ? "bg-primary text-white shadow-sm"
                            : "text-gray-800 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            active ? "bg-white/15" : "bg-primary/10 text-primary"
                          }`}
                        >
                          <Person className="w-5 h-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="font-medium block truncate">
                            {emp.name || emp.email || "Employee"}
                          </span>
                          {emp.email && emp.name && (
                            <span
                              className={`text-xs truncate block ${
                                active ? "text-white/80" : "text-gray-500"
                              }`}
                            >
                              {emp.email}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            {selectedEmployee && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-primary">
                  {selectedEmployee.name || selectedEmployee.email}
                </h2>
                <span className="text-sm text-gray-500">— goals for this employee</span>
              </div>
            )}
            {loading ? (
              <GoalsListSkeleton />
            ) : goals.length === 0 ? (
              emptyGoals
            ) : (
              goalsList
            )}
          </div>
        </div>
      ) : (
        <>
          {loading ? (
            <GoalsListSkeleton />
          ) : goals.length === 0 ? (
            emptyGoals
          ) : (
            goalsList
          )}
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete goal"
        message={
          deleteTarget
            ? `Delete “${deleteTarget.title}”? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
